import { useMutation, useQuery, useQueryClient } from "react-query";
import { createOrder, fetchOrders, patchOrders, fetchOrdersItems } from "./services/PurchaseService";
import { ALLOWED_PURCHASE_STATUS } from "../components/components.constants";

export const useOrders = (storeId, orderId, options = {}) => {
  const queryClient = useQueryClient();
  const { shouldFetchOrdersData = false } = options;

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const newOrder = await createOrder(payload);
      return newOrder;
    },
    onSuccess: (data, variables) => {
      const storeIdFromPayload = variables.storeId;

      queryClient.invalidateQueries(["orders", storeId]);

      const currentData = queryClient.getQueryData(['purchaseRecommendation', storeIdFromPayload]);

      if (!currentData) return;

      const finalizedProductIds = new Set( variables.items?.map(item => item.productId) || [] );

      queryClient.setQueryData(['purchaseRecommendation', storeIdFromPayload], (oldData) => {
        if (!oldData) return;

        const updatedRecommendations = oldData.purchaseRecommendation
          .map(item => finalizedProductIds.has(item.productId)
            ? { ...item, purchaseStatus: ALLOWED_PURCHASE_STATUS.FINALIZED }
            : item
          )
          .filter(item => item.purchaseStatus !== ALLOWED_PURCHASE_STATUS.FINALIZED);

        const newData = {
          ...oldData,
          purchaseRecommendation: updatedRecommendations,
          selectedSubgroups: [...new Set(updatedRecommendations.map(i => i.productSubgroupName).filter(Boolean))],
          selectedProducts: [...new Set(updatedRecommendations.map(i => i.productName).filter(Boolean))],
          productIdsToUse: [...new Set(updatedRecommendations.map(i => i.productId).filter(Boolean))],
          totalProducts: updatedRecommendations.length
        };

        return structuredClone(newData);
      });

    },
    ...options,
  });

  const patchMutation = useMutation({
    mutationFn: async (payload) => {
      const updatedOrder = await patchOrders(payload);
      return { 
        updatedOrder,
        orderId: payload.orderId
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["orders", storeId]);
      queryClient.invalidateQueries(["orderItems", orderId]);
    },
    ...options,
  });

  const query = useQuery({
    queryKey: ["orders", storeId],

    queryFn: async () => {

      if (!storeId) throw new Error("ID da loja é obrigatório");

      const apiData = await fetchOrders(storeId);
      return apiData;
    },

    enabled: Boolean(storeId) && shouldFetchOrdersData,
    keepPreviousData: false,
    staleTime: 0,
    cacheTime: 0,

    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    onError: (error) => {
      console.error("Erro ao carregar pedidos:", error);

      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/";
      }
    },

    ...options,
  });

  const orderItemsData = useQuery({
    queryKey: ["orderItems", orderId],

    queryFn: async () => {

      if (!orderId) throw new Error("ID do pedido é obrigatório");
      
      const apiData = await fetchOrdersItems(orderId, storeId);
      
      return apiData;
    },

    enabled: Boolean(orderId) && Boolean(storeId),
    keepPreviousData: false,
    staleTime: 0,
    cacheTime: 0,

    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    onError: (error) => {
      console.error("Erro ao carregar itens do pedido:", error);

      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = "/";
      }
    },

    ...options,
  });  

  return { 
    createOrder: mutation.mutate,
    isCreatingOrder: mutation.isLoading,
    isCreateOrderSuccess: mutation.isSuccess,
    createdOrder: mutation.data,
    mutation, 
    patchOrders: {
      mutate: patchMutation.mutate,
      mutateAsync: patchMutation.mutateAsync,
      updateOrderItemStatus: patchMutation.mutateAsync,
      isUpdating: patchMutation.isLoading,
      errorOnUpdate: patchMutation.isError
    },
    patchMutation, 
    query,
    isFetchingOrders: query.isFetching,
    orderItemsData,
    refetchOrderItems: orderItemsData.refetch,
    isLoadingOrderItems: orderItemsData.isLoading,
    isLoadingOrders: query.isLoading,
    error: mutation.error,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess
  };
};